// API utility for frontend
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Get auth token from localStorage
const getToken = () => localStorage.getItem('admin_token');

// Generic fetch wrapper
async function apiFetch(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  // Add auth token if available
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    ...options,
    headers
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }
  
  return data;
}

// ============ AUTH API ============
export const authAPI = {
  login: async (email, password) => {
    const data = await apiFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    if (data.token) {
      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));
    }
    return data;
  },
  
  logout: () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  },
  
  check: async () => {
    try {
      return await apiFetch('/auth/check');
    } catch {
      return { authenticated: false };
    }
  },
  
  isLoggedIn: () => {
    return !!getToken();
  },
  
  getUser: () => {
    const user = localStorage.getItem('admin_user');
    return user ? JSON.parse(user) : null;
  }
};

// ============ BOOKINGS API ============
export const bookingsAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/bookings${query ? '?' + query : ''}`);
  },
  
  getById: (id) => apiFetch(`/bookings/${id}`),
  
  // Get booked (confirmed) time slots for a specific date
  getBookedSlots: (date) => apiFetch(`/bookings/slots?date=${date}`),
  
  create: (bookingData) => apiFetch('/bookings', {
    method: 'POST',
    body: JSON.stringify(bookingData)
  }),
  
  update: (id, updates) => apiFetch(`/bookings/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  }),
  
  delete: (id) => apiFetch(`/bookings/${id}`, { method: 'DELETE' })
};

// ============ SERVICES API ============
export const servicesAPI = {
  getAll: (activeOnly = false) => 
    apiFetch(`/services${activeOnly ? '?activeOnly=true' : ''}`),
  
  getById: (id) => apiFetch(`/services/${id}`),
  
  create: (serviceData) => apiFetch('/services', {
    method: 'POST',
    body: JSON.stringify(serviceData)
  }),
  
  update: (id, updates) => apiFetch(`/services/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  }),
  
  delete: (id) => apiFetch(`/services/${id}`, { method: 'DELETE' })
};

// ============ AVAILABILITY API ============
export const availabilityAPI = {
  getAll: (month, year) => {
    const params = {};
    if (month) params.month = month;
    if (year) params.year = year;
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/availability${query ? '?' + query : ''}`);
  },
  
  update: (date, available, slots, note) => apiFetch('/availability', {
    method: 'POST',
    body: JSON.stringify({ date, available, slots, note })
  })
};

// ============ PHOTOS API ============
export const photosAPI = {
  getAll: (params = {}, fetchOptions = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/photos${query ? '?' + query : ''}`, fetchOptions);
  },
  
  getById: (id) => apiFetch(`/photos/${id}`),
  
  upload: (imageData, metadata = {}) => apiFetch('/photos', {
    method: 'POST',
    body: JSON.stringify({ imageData, ...metadata })
  }),
  
  update: (id, updates) => apiFetch(`/photos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  }),
  
  delete: (id) => apiFetch(`/photos/${id}`, { method: 'DELETE' }),
  
  reorder: (photoOrders) => apiFetch('/photos/reorder', {
    method: 'PUT',
    body: JSON.stringify({ photoOrders })
  })
};

// ============ VIDEOS API ============
export const videosAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/videos${query ? '?' + query : ''}`);
  },
  
  getById: (id) => apiFetch(`/videos/${id}`),
  
  // Upload video file to Cloudinary
  upload: (videoData, metadata = {}) => apiFetch('/videos', {
    method: 'POST',
    body: JSON.stringify({ videoData, ...metadata })
  }),
  
  create: (videoData) => apiFetch('/videos', {
    method: 'POST',
    body: JSON.stringify(videoData)
  }),
  
  update: (id, updates) => apiFetch(`/videos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  }),
  
  delete: (id) => apiFetch(`/videos/${id}`, { method: 'DELETE' })
};

// ============ CONTENT API ============
export const contentAPI = {
  getAll: () => apiFetch('/content'),
  
  getSection: (section) => apiFetch(`/content?section=${section}`),
  
  update: (section, data) => apiFetch('/content', {
    method: 'POST',
    body: JSON.stringify({ section, data })
  })
};

// ============ UPLOAD API (for direct Cloudinary uploads) ============
export const uploadAPI = {
  // Get signature for direct Cloudinary upload
  getSignature: (folder = 'reels') => apiFetch('/upload-signature', {
    method: 'POST',
    body: JSON.stringify({ folder })
  }),
  
  // Upload file directly to Cloudinary (bypasses server timeout issues)
  uploadToCloudinary: async (file, folder = 'reels', resourceType = 'video', onProgress) => {
    // Get signature from our API
    const signatureData = await uploadAPI.getSignature(folder);
    
    // Create form data for Cloudinary
    // Only include params that were signed: file, api_key, timestamp, signature, folder
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', signatureData.apiKey);
    formData.append('timestamp', signatureData.timestamp);
    formData.append('signature', signatureData.signature);
    formData.append('folder', signatureData.folder);
    
    // Upload directly to Cloudinary (resource_type is in URL, not form data)
    const cloudinaryUrl = `https://api.cloudinary.com/v1_1/${signatureData.cloudName}/${resourceType}/upload`;
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.responseText);
          resolve({
            url: response.secure_url,
            publicId: response.public_id,
            duration: response.duration,
            width: response.width,
            height: response.height
          });
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      });
      
      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
      
      xhr.open('POST', cloudinaryUrl);
      xhr.send(formData);
    });
  }
};

// ============ GALLERY API ============
export const galleryAPI = {
  getAll: (category) => {
    const query = category && category !== 'all' ? `?category=${category}` : '';
    return apiFetch(`/gallery${query}`);
  },
  
  getById: (id) => apiFetch(`/gallery/${id}`),
  
  create: (itemData) => apiFetch('/gallery', {
    method: 'POST',
    body: JSON.stringify(itemData)
  }),
  
  update: (id, updates) => apiFetch(`/gallery/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  }),
  
  delete: (id) => apiFetch(`/gallery/${id}`, { method: 'DELETE' })
};

// ============ SESSION PACKAGES API ============
export const packagesAPI = {
  getAll: (activeOnly = false) => 
    apiFetch(`/packages${activeOnly ? '?activeOnly=true' : ''}`),
  
  getById: (id) => apiFetch(`/packages/${id}`),
  
  create: (packageData) => apiFetch('/packages', {
    method: 'POST',
    body: JSON.stringify(packageData)
  }),
  
  update: (id, updates) => apiFetch(`/packages/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  }),
  
  delete: (id) => apiFetch(`/packages/${id}`, { method: 'DELETE' })
};

// ============ BOOKING ADDONS API ============
export const addonsAPI = {
  getAll: (activeOnly = false) => 
    apiFetch(`/addons${activeOnly ? '?activeOnly=true' : ''}`),
  
  getById: (id) => apiFetch(`/addons/${id}`),
  
  create: (addonData) => apiFetch('/addons', {
    method: 'POST',
    body: JSON.stringify(addonData)
  }),
  
  update: (id, updates) => apiFetch(`/addons/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  }),
  
  delete: (id) => apiFetch(`/addons/${id}`, { method: 'DELETE' })
};

const api = {
  auth: authAPI,
  bookings: bookingsAPI,
  services: servicesAPI,
  availability: availabilityAPI,
  photos: photosAPI,
  videos: videosAPI,
  content: contentAPI,
  gallery: galleryAPI,
  packages: packagesAPI,
  addons: addonsAPI
};

export default api;
